import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CredentialDetailPanel = ({ credential, onClose, onShare, onDownload, onRevoke }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showCryptographicDetails, setShowCryptographicDetails] = useState(false);

  if (!credential) return null;

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success bg-success/10';
      case 'expired': return 'text-error bg-error/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'revoked': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'metadata', label: 'Metadata', icon: 'Info' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Basic Information */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Basic Information</h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Credential ID:</span>
            <span className="text-foreground font-mono text-sm">{credential?.id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Type:</span>
            <span className="text-foreground">{credential?.type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(credential?.status)}`}>
              {credential?.status}
            </span>
          </div>
        </div>
      </div>

      {/* Issuer Information */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Issuer Information</h4>
        <div className="flex items-start space-x-4 p-4 bg-muted rounded-lg">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-background flex-shrink-0">
            <Image
              src={credential?.issuer?.logo}
              alt={credential?.issuer?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h5 className="font-medium text-foreground">{credential?.issuer?.name}</h5>
            <p className="text-sm text-muted-foreground mt-1">{credential?.issuer?.description}</p>
            <div className="flex items-center mt-2 space-x-4">
              <div className="flex items-center space-x-1">
                <Icon name="Globe" size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{credential?.issuer?.website}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="ShieldCheck" size={14} className="text-success" />
                <span className="text-xs text-success">Verified Issuer</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credential Claims */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Credential Claims</h4>
        <div className="space-y-3">
          {credential?.claims?.map((claim, index) => (
            <div key={index} className="flex justify-between py-2 border-b border-border last:border-b-0">
              <span className="text-muted-foreground">{claim?.name}:</span>
              <span className="text-foreground font-medium">{claim?.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMetadataTab = () => (
    <div className="space-y-6">
      {/* Dates */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Important Dates</h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Issued Date:</span>
            <span className="text-foreground">{formatDate(credential?.issuedDate)}</span>
          </div>
          {credential?.expirationDate && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Expiration Date:</span>
              <span className="text-foreground">{formatDate(credential?.expirationDate)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Last Verified:</span>
            <span className="text-foreground">{formatDate(credential?.lastVerified)}</span>
          </div>
        </div>
      </div>

      {/* Schema Information */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Schema Information</h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Schema Version:</span>
            <span className="text-foreground font-mono">{credential?.schemaVersion}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Format:</span>
            <span className="text-foreground">{credential?.format}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Encoding:</span>
            <span className="text-foreground">{credential?.encoding}</span>
          </div>
        </div>
      </div>

      {/* Additional Metadata */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Additional Metadata</h4>
        <div className="bg-muted rounded-lg p-4">
          <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-mono">
            {JSON.stringify(credential?.metadata, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      {/* Cryptographic Proof */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-foreground">Cryptographic Proof</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCryptographicDetails(!showCryptographicDetails)}
          >
            <Icon name={showCryptographicDetails ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-success/10 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="ShieldCheck" size={20} className="text-success" />
              <span className="text-success font-medium">Cryptographically Signed</span>
            </div>
            <Icon name="CheckCircle" size={16} className="text-success" />
          </div>

          {showCryptographicDetails && (
            <div className="space-y-3 mt-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Signature Algorithm:</span>
                <span className="text-foreground font-mono">{credential?.signatureAlgorithm}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Hash Algorithm:</span>
                <span className="text-foreground font-mono">{credential?.hashAlgorithm}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Digital Signature:</span>
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <code className="text-xs text-muted-foreground break-all font-mono">
                    {credential?.digitalSignature}
                  </code>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Blockchain Anchoring */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Blockchain Anchoring</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Link" size={20} className="text-primary" />
              <span className="text-primary font-medium">Anchored on Blockchain</span>
            </div>
            <Icon name="ExternalLink" size={16} className="text-primary" />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Network:</span>
              <span className="text-foreground">{credential?.blockchain?.network}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Transaction Hash:</span>
              <span className="text-foreground font-mono text-sm">{credential?.blockchain?.txHash}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Block Number:</span>
              <span className="text-foreground">{credential?.blockchain?.blockNumber}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Zero-Knowledge Proof */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Zero-Knowledge Proof</h4>
        <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Eye" size={20} className="text-accent" />
            <span className="text-accent font-medium">Privacy-Preserving Verification</span>
          </div>
          <Icon name="Lock" size={16} className="text-accent" />
        </div>
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-foreground">Verification History</h4>
      <div className="space-y-3">
        {credential?.verificationHistory?.map((event, index) => (
          <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              event?.type === 'verified' ? 'bg-success text-success-foreground' :
              event?.type === 'shared' ? 'bg-primary text-primary-foreground' :
              'bg-muted-foreground text-background'
            }`}>
              <Icon 
                name={
                  event?.type === 'verified' ? 'ShieldCheck' :
                  event?.type === 'shared' ? 'Share' :
                  event?.type === 'issued' ? 'FileText' : 'Clock'
                } 
                size={16} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{event?.action}</p>
              <p className="text-xs text-muted-foreground mt-1">{event?.description}</p>
              <p className="text-xs text-muted-foreground mt-1">{formatDate(event?.timestamp)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 lg:relative lg:inset-auto">
      {/* Mobile Overlay */}
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm lg:hidden" onClick={onClose} />
      {/* Panel Content */}
      <div className="fixed inset-x-0 bottom-0 top-16 bg-background lg:relative lg:inset-auto lg:top-0 lg:w-96 lg:border-l border-border overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={credential?.issuer?.logo}
                alt={credential?.issuer?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-foreground truncate">{credential?.title}</h3>
              <p className="text-sm text-muted-foreground truncate">{credential?.issuer?.name}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'metadata' && renderMetadataTab()}
          {activeTab === 'security' && renderSecurityTab()}
          {activeTab === 'history' && renderHistoryTab()}
        </div>

        {/* Actions */}
        <div className="border-t border-border p-6">
          <div className="flex flex-col space-y-3">
            <Button
              variant="default"
              onClick={() => onShare(credential)}
              iconName="Share"
              iconPosition="left"
              fullWidth
            >
              Share Credential
            </Button>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => onDownload(credential)}
                iconName="Download"
                iconPosition="left"
                className="flex-1"
              >
                Download
              </Button>
              <Button
                variant="destructive"
                onClick={() => onRevoke(credential)}
                iconName="Trash2"
                iconPosition="left"
                className="flex-1"
              >
                Revoke
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialDetailPanel;